import { onMounted, ref } from 'vue'
import { useErrorStore } from '@/stores/errorStore'

/**
 * Detects when a new app deployment happens and prompts user to refresh
 * This prevents "Failed to fetch dynamically imported module" errors
 * caused by stale chunk references after deployment
 */
export function useDeploymentDetection() {
  const errorStore = useErrorStore()
  let versionCheckInterval: ReturnType<typeof setInterval> | null = null
  const deploymentDetected = ref(false)

  onMounted(() => {
    // Get initial version hash from main entry point
    const getVersionHash = async (): Promise<string | null> => {
      try {
        // Fetch index.html to get current version (no cache)
        const response = await fetch(import.meta.env.BASE_URL, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
        })
        const html = await response.text()
        
        // Extract version from global constant injected into HTML
        // Pattern: window.__APP_VERSION__="0.0.X"
        const match = html.match(/window\.__APP_VERSION__="([^"]+)"/)
        return match ? match[1] : null
      } catch (error) {
        console.warn('Failed to check deployment version:', error)
        return null
      }
    }

    // Store initial version on app load
    // @ts-ignore - __APP_VERSION__ is defined by Vite define
    const initialVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null

    // Check every 60 seconds if deployment has happened
    versionCheckInterval = setInterval(async () => {
      if (!initialVersion) return
      
      const currentVersion = await getVersionHash()
      // Note: getVersionHash might still return null if it fails to fetch index.html
      if (currentVersion && currentVersion !== initialVersion) {
        console.warn('🚀 New deployment detected! Notifying user...')
        deploymentDetected.value = true
        
        errorStore.sendError({
          show: true,
          message: 'En ny version af appen er tilgængelig. Klik her for at indlæse den.' // A new version is available. Click here to load it.
        })

        // Listen for error click to reload
        // User will need to refresh when they see the message
        setTimeout(() => {
          if (deploymentDetected.value) {
            console.log('New deployment still detected, suggesting reload...')
          }
        }, 3000)

        // Clear interval to avoid repeated checks
        if (versionCheckInterval) {
          clearInterval(versionCheckInterval)
          versionCheckInterval = null
        }
      }
    }, 30000) // Check every 30 seconds
  })

  const reloadApp = () => {
    window.location.href = window.location.origin
  }

  return {
    deploymentDetected,
    reloadApp
  }
}
